import { useEffect, useRef, useState } from 'react'
import photos from '@/content/photos.json'
import type { Photo } from '@/content/sections'

/**
 * A video on a marketing page, playing itself when the reader reaches it.
 *
 * The first video on this site. Asked for by Fatih on 2026-08-30 for the DAIMI
 * and AIMI solution pages — *"ilgili yerlere bu videolar gömülü şekilde autoplay
 * olacak"* — and asked for once before, on 2026-08-27, when only a still frame
 * of it arrived (`videoframe_6257.png`, still the poster below).
 *
 * FOUR THINGS ARE LOAD-BEARING and none of them is decoration:
 *
 * 1. **`muted`, or it does not play at all.** Every browser blocks autoplay with
 *    sound. An unmuted `autoplay` is not a louder page; it is a page where the
 *    video silently never starts, which is the one outcome that looks like the
 *    feature was never built.
 *
 * 2. **`playsInline`, or iOS takes over the screen.** Without it Safari on a
 *    phone opens the video fullscreen the moment it plays — a reader scrolling
 *    past a panel gets a fullscreen film they did not ask for.
 *
 * 3. **It plays on INTERSECTION, not on load, with `preload="none"`.** These are
 *    marketing films several megabytes each, on pages a phone on mobile data
 *    reaches. A bare `autoplay` fetches the whole file on every visit whether or
 *    not the reader ever scrolls to it; this fetches nothing until the panel is
 *    actually on screen, and pauses again when it leaves. That is also why the
 *    poster matters — the panel is complete before a single byte of video moves.
 *
 * 4. **A way to pause, and it is an accessibility requirement rather than
 *    taste.** WCAG 2.2.2: motion that runs more than five seconds must be
 *    pausable. A looping autoplay film with no way to stop it fails that.
 *    It used to be the browser's own `controls` bar; Fatih asked on 2026-08-30
 *    for the film to *"kendi kendine loopta dönsün"* without the scrubber, the
 *    timecode and the hover chrome that came with it. So the bar is gone and
 *    the requirement is met by one small always-visible pause button in the
 *    corner instead — always visible on purpose, because a control that only
 *    appears on hover does not exist on a touch screen. Do not remove it and
 *    do not hide it behind `group-hover`; there is no third option that still
 *    passes 2.2.2.
 *
 * And `prefers-reduced-motion` is honoured: a reader who has asked their system
 * not to animate things gets the poster and a play button, not a film that
 * starts itself.
 */
type Manifest = Record<string, { width: number; height: number; fallback: string }>

export default function SiteVideo({
  src,
  poster,
  title,
  className,
}: {
  /** The file's own URL. These live in the `website-documents` bucket, not the repo. */
  src: string
  /**
   * The frame to hold until it plays — and what a reader who blocks video, or
   * asks for reduced motion, sees instead. A `Photo`, so it comes out of the
   * image manifest at a sensible size like every other picture on the site.
   */
  poster?: Photo
  /** What the film shows, for anyone who cannot watch it. */
  title: string
  className?: string
}) {
  const ref = useRef<HTMLVideoElement>(null)
  // Read once, at mount: this decides whether the observer is armed at all, and
  // a value that changed under it would leave a film playing for somebody who
  // asked for stillness.
  const [reduced] = useState(
    () => typeof matchMedia === 'function'
      && matchMedia('(prefers-reduced-motion: reduce)').matches,
  )
  const [playing, setPlaying] = useState(false)
  // Somebody who pressed pause meant it. Without this the observer starts the
  // film again the next time the panel re-enters the viewport, which reads as
  // the button not working.
  const stopped = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el || reduced || typeof IntersectionObserver !== 'function') return

    const seen = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (stopped.current) return
          // A rejected play() is not an error worth surfacing — a browser is
          // entitled to refuse, and the poster is already the right fallback.
          void el.play().catch(() => {})
        } else {
          el.pause()
        }
      },
      // Half of it on screen, so a panel clipping into view at the bottom of the
      // window does not start a film the reader has not arrived at yet.
      { threshold: 0.5 },
    )
    seen.observe(el)
    return () => seen.disconnect()
  }, [reduced])

  const toggle = () => {
    const el = ref.current
    if (!el) return
    if (el.paused) {
      stopped.current = false
      void el.play().catch(() => {})
    } else {
      stopped.current = true
      el.pause()
    }
  }

  const still = poster ? (photos as Manifest)[poster.src]?.fallback : undefined

  return (
    <div className={`relative overflow-hidden ${className ?? ''}`}>
      <video
        ref={ref}
        className="block w-full"
        // `autoPlay` is deliberately NOT set — the observer owns starting it, so
        // the file is not fetched for a reader who never scrolls this far.
        muted
        loop
        playsInline
        preload="none"
        poster={still}
        aria-label={title}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      >
        <source src={src} type="video/mp4" />
        {title}
      </video>
      {/* The whole of WCAG 2.2.2 for this panel. Small, low-contrast and out of
          the way, but always on screen — never hover-revealed. */}
      <button
        type="button"
        onClick={toggle}
        // English, like the browser's own control bar was before it: the label
        // is read by a screen reader beside `title`, which is already the
        // page's own language, and one word did not justify a key in all six
        // dictionaries.
        aria-label={`${playing ? 'Pause' : 'Play'}: ${title}`}
        className="absolute bottom-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white opacity-70 backdrop-blur-sm transition hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4" fill="currentColor">
          {playing
            ? <path d="M9 5h2.5v14H9zm3.5 0H15v14h-2.5z" />
            : <path d="M8 5l11 7-11 7z" />}
        </svg>
      </button>
    </div>
  )
}
